// Copyright (c) 2021 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, debounceTime, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as ToolbarActions from "../actions"
import * as ToolbarSlice from "../slice"
import { SearchTerms } from "../slice"
import {
  CountFilteredTimeRowsRequest,
  CountFilteredTimeRowsResponse,
} from "../../../../generated/wisetime/user/timeline/v4/timeline_pb"

import { grpc } from "../../../../app/rpc/executor"
import { timelineClient } from "../../../../app/rpc/client"
import { RootState } from "../../../../app/store"
import { getCurrentDate, getWeekRange, isFutureDate, toDate } from "../../dateTime"
import { getMergedFilter, isSearchNotEmpty } from "../../search-bar/searchUtils"
import { debug } from "../../../../app/logging"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../../snackbar/actions"

type CountFilteredTimeRowsRpc = (request: CountFilteredTimeRowsRequest) => Observable<CountFilteredTimeRowsResponse>

const createRequest = (searchTerms: SearchTerms, state: RootState): Observable<CountFilteredTimeRowsRequest> => {
  const week = getWeekRange(toDate(state.timeline.activeDate))
  if (isFutureDate(week.startDateLocal) || !state.timeline.timeZone) {
    return EMPTY
  }

  const filter = getMergedFilter(searchTerms, state.userContext.currentSiteId)
  const req = new CountFilteredTimeRowsRequest()
    .setStartDate(week.startDateLocal.toString())
    .setEndDate(isFutureDate(week.endDateLocal) ? getCurrentDate().toString() : week.endDateLocal.toString())
    .setFilter(
      new CountFilteredTimeRowsRequest.Filter()
        .setActivitiesList(filter.activities)
        .setDescriptionsList(filter.descriptions)
        .setTagIdsList(filter.tags.map(tag => tag.id))
    )
  debug(JSON.stringify(req.toObject()))
  return of(req)
}

const isActionMatching = <T>(action: Action<T>): boolean => {
  // count filtered time rows on filter change
  return ToolbarSlice.addSearchActivity.match(action) ||
    ToolbarSlice.addSearchDescription.match(action) ||
    ToolbarSlice.addSearchTag.match(action) ||
    ToolbarSlice.removeSearchTerm.match(action) ||
    ToolbarSlice.addBroadSearch.match(action) ||
    ToolbarSlice.addTermsSearch.match(action) ||
    // count filtered time rows on demand
    ToolbarActions.countFilteredTimeRowsByDay.match(action)
}

export const countFilteredTimeRowsByDay = (countFilteredTimeRowsRpc: CountFilteredTimeRowsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(action => isActionMatching(action)),
      debounceTime(200),
      withLatestFrom(state$),
      mergeMap(([, state]) => of(state.toolbar.searchTerms).pipe(
        filter(isSearchNotEmpty),
        mergeMap(filter => createRequest(filter, state)),
        mergeMap(countFilteredTimeRowsRpc),
        map(t => t.toObject()),
        map(ToolbarSlice.setSearchMatches),
        catchError(handleError)),
      )
    )

const handleError = (): Observable<Action> =>
  of(showSnackBar({
    message: t({ id: "msg_count_filtered_time_rows_failed", message: "Failed to count search matches. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))

const countFilteredTimeRowsRpc = (request: CountFilteredTimeRowsRequest): Observable<CountFilteredTimeRowsResponse> =>
  grpc(() => timelineClient.countFilteredTimeRows(request, null))

export default countFilteredTimeRowsByDay(countFilteredTimeRowsRpc)
